import { useCallback, useMemo, useSyncExternalStore } from "react";
import { quantity, type Quantity } from "@tovi/shared";
import { priceOrder, type OrderLine, type PricedOrder, type PricingInput } from "@tovi/pos-core";
import type { BillBookStore } from "./bill-book.ts";
import { activeBill, type Bill } from "./bills.ts";

/**
 * The cart, priced locally (`02-architecture.md` § `packages/pos-core`).
 *
 * Every figure on this screen comes from `priceOrder` — the same function the
 * server calls when the bill is settled. That is the whole reason `pos-core`
 * exists: a till that computed its own totals would eventually disagree with
 * the receipt, and the customer is holding the receipt.
 *
 * It also means the cart prices with no network at all, which is what makes
 * offline ordering possible rather than merely queued.
 */

/** A chosen option: the word the station needs, and the money the bill does. */
export type LineOption = { readonly name: string; readonly price: bigint };

export type CartLine = OrderLine & {
  readonly key: string;
  readonly name: string;
  /** Which batch it belongs to: § A distinguishes "Latest Order" from "Ordered". */
  readonly isFired: boolean;
  /** What the sheet collected for the station. Absent rather than empty. */
  readonly note?: string;
  /**
   * What the sheet chose, by name. `modifiers` carries the same options'
   * prices for the engine; these carry their words for the cart line, the
   * ticket and `OrderLineInput` — which keeps them in two fields too.
   *
   * `unitPrice + Σ modifiers` is what one of these costs, on every line. A
   * line adopted from the server carries the names here and nothing in
   * `modifiers`: the price it came back with already has them in it.
   */
  readonly specifications?: readonly LineOption[];
  readonly toppings?: readonly LineOption[];
};

/** Every option on a line, specifications first, in the order the sheet asked. */
export function optionsOf(line: CartLine): readonly LineOption[] {
  return [...(line.specifications ?? []), ...(line.toppings ?? [])];
}

/**
 * What one of these costs, before quantity — `unit_price + Σ modifiers`, which
 * is the engine's own rule (`pos-core/types.ts`) and the only one that answers
 * for a local line and an adopted one at once.
 *
 * Not the sum of `optionsOf`: an adopted line names its options and was sent
 * back with their prices already in `unitPrice`, so adding them here would
 * charge each of them twice on a bill somebody is about to pay.
 */
export function unitCost(line: CartLine): bigint {
  return (line.modifiers ?? []).reduce(
    (sum, modifier) => sum + modifier.amount,
    line.unitPrice.amount,
  );
}

export type Seat = { readonly tableId: string; readonly tableName: string } | null;

export type Cart = {
  readonly lines: readonly CartLine[];
  /** The bill these lines belong to. Null until one is open. */
  readonly bill: Bill | null;
  readonly table: Seat;
  readonly covers: number;
  readonly currency: string;
  readonly priced: PricedOrder | null;
  readonly add: (line: CartLine) => void;
  readonly addOrIncrement: (line: CartLine) => void;
  readonly remove: (key: string) => void;
  readonly setQuantity: (key: string, quantity: number) => void;
  readonly decrement: (key: string) => void;
  readonly discount: (amount: bigint) => void;
  readonly discountAmount: bigint;
  readonly fire: () => void;
  /** Park the open bill, or bring it back. Null when no bill is open. */
  readonly setHeld: (held: boolean) => void;
  /** Take the tax off the open bill, or put it back. */
  readonly setTaxExempt: (exempt: boolean) => void;
  /** Void or return a line the station already has, by its server id. */
  readonly amendLine: (itemId: string, action: "void" | "return") => void;
  readonly unfired: readonly CartLine[];
};

/** Everything the engine needs that is not the cart itself. */
export type PricingContext = Omit<PricingInput, "lines" | "billDiscount">;

/**
 * The same thing twice: the rule a repeat tap on a tile merges by.
 *
 * Unfired, because the station already has anything else — taking a fired line
 * from 2 to 3 is a course whose ticket never printed. No modifiers and no note,
 * because a line carrying either was specified deliberately and a tap on a tile
 * is not that specification: without the note in this rule, a tap lands
 * silently on somebody's "no ice".
 */
function absorbs(held: CartLine, line: CartLine): boolean {
  return held.id === line.id && !held.isFired && (held.modifiers?.length ?? 0) === 0 && !held.note;
}

/**
 * Quantity carries its own scale, so two have to be aligned before they can be
 * added: `{ 2n, 0 }` and `{ 500n, 3 }` are 2 and 0.5, not 502 of anything.
 * Every line the till makes today is scale 0 — an item sold by weight is not.
 */
function plus(a: Quantity, b: Quantity): Quantity {
  const scale = Math.max(a.scale, b.scale);
  const lift = (q: Quantity) => q.value * 10n ** BigInt(scale - q.scale);
  return quantity(lift(a) + lift(b), scale);
}

/**
 * Add a line, or raise the one already standing for it.
 *
 * The last match rather than the first: a cashier who has opened a second line
 * for the same item is working on the second one.
 *
 * Exported for its test — `useCart` needs a renderer, and this rule is the part
 * that has to be right.
 */
export function addOrIncrementLines(held: readonly CartLine[], line: CartLine): CartLine[] {
  const at = held.findLastIndex((row) => absorbs(row, line));
  if (at === -1) return [...held, line];
  return held.map((row, index) =>
    index === at ? { ...row, quantity: plus(row.quantity, line.quantity) } : row,
  );
}

/**
 * Take one off a line, and take the line off when the last one goes.
 *
 * A stepper that stops at one leaves a mis-tapped item on the bill with no way
 * off it, so the bottom of the range is a removal rather than a floor. Unfired
 * only, for the same reason `remove` is: the station already has anything else,
 * and taking that off is a void the order surface audits.
 *
 * Exported for its test, like `addOrIncrementLines` above it.
 */
export function decrementLines(held: readonly CartLine[], key: string): CartLine[] {
  return held.flatMap((row) => {
    if (row.key !== key || row.isFired) return [row];
    const left = row.quantity.value - 10n ** BigInt(row.quantity.scale);
    if (left <= 0n) return [];
    return [{ ...row, quantity: quantity(left, row.quantity.scale) }];
  });
}

/**
 * The cart, over whichever bill is active.
 *
 * The lines live in the bill book rather than in this hook, because a till
 * holds several bills at once and a cashier switching between them must not
 * find the wrong party's food on the screen. What stays here is the pricing
 * and the rules about which line a tap lands on.
 *
 * Unfired lines are still local, deliberately — see `bills.ts`.
 */
export function useCart(context: PricingContext | undefined, store: BillBookStore): Cart {
  const book = useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => store.book(),
    () => store.book(),
  );
  const bill = activeBill(book);
  const lines = bill?.lines ?? EMPTY_LINES;
  const discountAmount = bill?.discountAmount ?? 0n;

  const priced = useMemo(() => {
    if (!context || !bill || lines.length === 0) return null;
    return priceOrder({
      ...context,
      // The bill's own choice, not a guess from whether a table is set. A
      // cashier who forgot to seat a table used to have the party silently
      // rung up as take-away, losing the service charge.
      diningStyle: bill.diningStyle,
      // Also the bill's own choice rather than the store's default, for the
      // same reason: an exemption belongs to this party, not to the terminal.
      isTaxExempt: bill.isTaxExempt,
      lines: [...lines],
      billDiscount:
        discountAmount > 0n ? { amount: discountAmount, currency: context.currency } : undefined,
    });
  }, [lines, context, discountAmount, bill]);

  const add = useCallback(
    (line: CartLine) => {
      void store.setLines([...lines, line]);
    },
    [store, lines],
  );

  const addOrIncrement = useCallback(
    (line: CartLine) => {
      void store.setLines(addOrIncrementLines(lines, line));
    },
    [store, lines],
  );

  const remove = useCallback(
    (key: string) => {
      // Only an unfired line can simply go: once the station has it, taking it
      // off is a void, which is audited and belongs to the order surface.
      void store.setLines(lines.filter((line) => line.key !== key || line.isFired));
    },
    [store, lines],
  );

  const setQuantity = useCallback(
    (key: string, whole: number) => {
      void store.setLines(
        lines.map((line): CartLine =>
          line.key === key && !line.isFired
            ? { ...line, quantity: quantity(BigInt(Math.max(1, whole))) }
            : line,
        ),
      );
    },
    [store, lines],
  );

  const decrement = useCallback(
    (key: string) => {
      void store.setLines(decrementLines(lines, key));
    },
    [store, lines],
  );

  return {
    lines,
    bill,
    table: bill?.table ? { tableId: bill.table.id, tableName: bill.table.name } : null,
    covers: bill?.covers ?? 1,
    currency: context?.currency ?? "USD",
    priced,
    discountAmount,
    unfired: lines.filter((line) => !line.isFired),
    add,
    addOrIncrement,
    remove,
    setQuantity,
    decrement,
    discount: (amount: bigint) => void store.setDiscount(amount),
    // Firing is a queued operation now, not a local flag: "Ordered" has to
    // mean the station has it.
    fire: () => void store.fire(),
    setHeld: (held: boolean) => {
      if (bill) void store.setHeld(bill.key, held);
    },
    setTaxExempt: (exempt: boolean) => {
      if (bill) void store.setTaxExempt(bill.key, exempt);
    },
    amendLine: (itemId: string, action: "void" | "return") => {
      if (bill) void store.amendLine(bill.key, itemId, action);
    },
  };
}

const EMPTY_LINES: readonly CartLine[] = [];
